import { yupResolver } from '@hookform/resolvers/yup';
import { isEmpty } from 'lodash';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { DailySchedule, IProgramSetup, IProgramSetupForm } from 'shared/interfaces/IProgramSetup';
import * as Yup from 'yup';

interface Props {
  programSetup?: IProgramSetup;
}

export const useProgramSetupForm = ({ programSetup }: Props) => {
  const getInitialValue = (
    key: keyof IProgramSetup,
    defaultValue: any,
    cb?: (key: string | number | symbol, record: IProgramSetup) => any,
  ) => {
    if (programSetup) {
      if (cb) {
        const value = cb(key, programSetup);
        return value;
      }
      return programSetup[key];
    } else {
      return defaultValue;
    }
  };

  // const getDailyScheduleData = (dailySchedule?: DailySchedule) => {
  //   if (!dailySchedule) return undefined;

  //   const schedule: DailySchedule = dailySchedule;

  //   for (const day in schedule) {
  //     if (
  //       !schedule[day]?.timeslot_start.includes('1990-01-01') ||
  //       !schedule[day]?.timeslot_end.includes('1990-01-01')
  //     ) {
  //       schedule[day].timeslot_start = `1990-01-01 ${schedule[day]?.timeslot_start}`;
  //       schedule[day].timeslot_end = `1990-01-01 ${schedule[day]?.timeslot_end}`;
  //     }
  //   }

  //   return schedule;
  // };
  const getDailyScheduleData = (dailySchedule?: DailySchedule) => {
    if (!dailySchedule) return undefined;

    const schedule: DailySchedule = dailySchedule;

    const withDate = (time?: string | null) => (time && !time.includes('1990-01-01') ? `1990-01-01 ${time}` : time);

    for (const day in schedule) {
      if (schedule[day]) {
        schedule[day].timeslot_start = withDate(schedule[day].timeslot_start);
        schedule[day].timeslot_end = withDate(schedule[day].timeslot_end);
      }
    }

    return schedule;
  };

  const getChapterScheduleData = (chapterSchedule?: any) => {
    if (!chapterSchedule) return undefined;
    const schedule = { ...chapterSchedule };
    for (const day in schedule) {
      if (schedule[day]?.chapter_start && !schedule[day].chapter_start.includes('1990-01-01')) {
        schedule[day].chapter_start = `1990-01-01 ${schedule[day].chapter_start}`;
      }
      if (schedule[day]?.chapter_end && !schedule[day].chapter_end.includes('1990-01-01')) {
        schedule[day].chapter_end = `1990-01-01 ${schedule[day].chapter_end}`;
      }
    }
    return schedule;
  };

  // Existing records store these as null (not just absent) for anything never
  // filled in via the now-removed Title Location OCR cards — .notRequired() alone
  // only permits undefined, so every field here also needs .nullable() or editing
  // an old record throws "must be a `string` type ... final value was: `null`".
  const titleSetupObject = {
    ocr_lang: Yup.string().label('OCR Language').notRequired().nullable(),
    top_left_up: Yup.string().label('Top Left').notRequired().nullable(),
    top_left_down: Yup.string().label('Bottom Left').notRequired().nullable(),
    top_right_up: Yup.string().label('Top Right').notRequired().nullable(),
    top_right_down: Yup.string().label('Bottom Right').notRequired().nullable(),
    on_off: Yup.mixed().oneOf([1, 0, null]).label('Status').notRequired().nullable(),
    machine_ip_address: Yup.string()
      .matches(
        /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/,
        {
          message: 'Invalid IP address',
          excludeEmptyString: true,
        },
      )
      .label('Machine IP Address')
      .notRequired()
      .nullable(),
    minimum_article_length_in_seconds: Yup.number().label('Minimum Article Length').notRequired().nullable(),
  };

  const recordingTitleSetupObject = {
    folder_name: Yup.string().label('Folder Name').notRequired().nullable().default(''),
    file_extension: Yup.string().label('File Extension').notRequired().nullable().default(''),
    recording_machine_name: Yup.string().label('Recording Machine Name').notRequired().nullable().default(''),
    filename: Yup.string().label('Filename').notRequired().nullable().default(''),
    sagap_process: Yup.boolean().label('Sagap Process').notRequired().nullable().default(false),
  };

  const ProgramSetupSchema: Yup.SchemaOf<IProgramSetupForm> = Yup.object({
    anchors: Yup.array().min(1).required().label('Anchors'),
    channel_obj: Yup.string().label('TV Channel'),
    program_obj: Yup.string().label('TV Program'),
    station_obj: Yup.string().notRequired().nullable().label('Radio Station'),
    station_program_obj: Yup.mixed()
      .when('station_obj', {
        is: (channel) => isEmpty(channel),
        then: Yup.string().notRequired().nullable(),
        otherwise: Yup.string().required().typeError('Radio Station Program is Required'),
      })
      .label('Radio Station Program'),

    country: Yup.string().required().label('Country'),
    segment_type: Yup.string().required().nullable().label('Segment Type'),
    program_type: Yup.string().notRequired().nullable().label('Program Type'),
    is_simulcast: Yup.boolean().notRequired(),

    youtube_scrape_details: Yup.object({
      youtube_url: Yup.string().nullable(),
      youtube_channel_filter: Yup.string().nullable(),
      youtube_channel_name: Yup.string().nullable(),
      youtube_last_upload_url: Yup.string().nullable(),
    }),
    facebook_scraper_details: Yup.object({
      facebook_url: Yup.string().nullable(),
      facebook_last_upload_url: Yup.string().nullable(),
    }),
    local_recording_details: Yup.object({
      local_recording_path: Yup.string().nullable().label('Local Recording Path'),
      source_folder: Yup.string().notRequired().nullable().label('Source Folder'),
    }),
    // Existing records store null (not just absent) for slots nobody's touched —
    // each field needs its own .nullable(), it doesn't inherit from the parent.
    daily_schedule: Yup.object({
      monday: Yup.object({
        timeslot_start: Yup.string().nullable(),
        timeslot_end: Yup.string().nullable(),
        automation_enabled: Yup.boolean().notRequired().nullable().default(false),
      }),
      tuesday: Yup.object({
        timeslot_start: Yup.string().nullable(),
        timeslot_end: Yup.string().nullable(),
        automation_enabled: Yup.boolean().notRequired().nullable().default(false),
      }),
      wednesday: Yup.object({
        timeslot_start: Yup.string().nullable(),
        timeslot_end: Yup.string().nullable(),
        automation_enabled: Yup.boolean().notRequired().nullable().default(false),
      }),
      thursday: Yup.object({
        timeslot_start: Yup.string().nullable(),
        timeslot_end: Yup.string().nullable(),
        automation_enabled: Yup.boolean().notRequired().nullable().default(false),
      }),
      friday: Yup.object({
        timeslot_start: Yup.string().nullable(),
        timeslot_end: Yup.string().nullable(),
        automation_enabled: Yup.boolean().notRequired().nullable().default(false),
      }),
      saturday: Yup.object({
        timeslot_start: Yup.string().nullable(),
        timeslot_end: Yup.string().nullable(),
        automation_enabled: Yup.boolean().notRequired().nullable().default(false),
      }),
      sunday: Yup.object({
        timeslot_start: Yup.string().nullable(),
        timeslot_end: Yup.string().nullable(),
        automation_enabled: Yup.boolean().notRequired().nullable().default(false),
      }),
    }),
    chapter_schedule: Yup.object({
      monday: Yup.object({
        chapter_start: Yup.string().nullable(),
        chapter_end: Yup.string().nullable(),
      }),
      tuesday: Yup.object({
        chapter_start: Yup.string().nullable(),
        chapter_end: Yup.string().nullable(),
      }),
      wednesday: Yup.object({
        chapter_start: Yup.string().nullable(),
        chapter_end: Yup.string().nullable(),
      }),
      thursday: Yup.object({
        chapter_start: Yup.string().nullable(),
        chapter_end: Yup.string().nullable(),
      }),
      friday: Yup.object({
        chapter_start: Yup.string().nullable(),
        chapter_end: Yup.string().nullable(),
      }),
      saturday: Yup.object({
        chapter_start: Yup.string().nullable(),
        chapter_end: Yup.string().nullable(),
      }),
      sunday: Yup.object({
        chapter_start: Yup.string().nullable(),
        chapter_end: Yup.string().nullable(),
      }),
    }).notRequired(),

    // Title Location (OCR screenshot) UI was removed — no form can ever satisfy the
    // "otherwise" required branch these used to have, so they're unconditionally
    // optional now (matches TV's schema). Existing records keep their saved values.
    youtube_title_setup: Yup.object({ ...titleSetupObject })
      .notRequired()
      .nullable(),
    facebook_title_setup: Yup.object({ ...titleSetupObject })
      .notRequired()
      .nullable(),
    recording_title_setup: Yup.object({ ...titleSetupObject, ...recordingTitleSetupObject })
      .notRequired()
      .nullable(),
  });

  const defaultValues: IProgramSetupForm = {
    anchors: getInitialValue('anchors', [], (_, record: IProgramSetup) => record.anchors?.map((anchor) => anchor?._id)),
    channel_obj: getInitialValue(
      'channel_details',
      undefined,
      (_, record: IProgramSetup) => record.channel_details?._id,
    ),
    program_obj: getInitialValue(
      'program_details',
      undefined,
      (_, record: IProgramSetup) => record.station_program_details?._id,
    ),
    station_obj: getInitialValue(
      'station_details',
      null,
      (_, record: IProgramSetup) => record.station_details?._id || null,
    ),
    station_program_obj: getInitialValue('station_program_details', null, (_, record: IProgramSetup) => {
      return record.station_program_details?._id || null;
    }),
    youtube_scrape_details: getInitialValue('youtube_scrape_details', {
      youtube_url: '',
      youtube_channel_filter: 'video',
      youtube_channel_name: '',
      youtube_last_upload_url: '',
    }),
    facebook_scraper_details: getInitialValue('facebook_scraper_details', {
      facebook_url: '',
      facebook_last_upload_url: '',
    }),
    local_recording_details: getInitialValue('local_recording_details', {
      local_recording_path: '',
    }),
    country: getInitialValue('country', 'PHILIPPINES'),
    segment_type: getInitialValue('segment_type', null),
    // Filled from the selected program once it loads (see ProgramSetupContainer).
    program_type: null,
    youtube_title_setup: getInitialValue('youtube_title_setup', null),
    facebook_title_setup: getInitialValue('facebook_title_setup', null),
    // Status and Sagap Process are per-program (edited in Program Details); unset ones read as Active / No.
    recording_title_setup: {
      folder_name: null,
      file_extension: null,
      recording_machine_name: null,
      filename: null,
      ocr_lang: 'eng',
      ...programSetup?.recording_title_setup,
      on_off: programSetup?.recording_title_setup?.on_off === 0 ? 0 : 1,
      sagap_process: programSetup?.recording_title_setup?.sagap_process === true,
    },
    daily_schedule: getInitialValue(
      'daily_schedule',
      {
        monday: {
          timeslot_start: '',
          timeslot_end: '',
        },
        tuesday: {
          timeslot_start: '',
          timeslot_end: '',
        },
        wednesday: {
          timeslot_start: '',
          timeslot_end: '',
        },
        thursday: {
          timeslot_start: '',
          timeslot_end: '',
        },
        friday: {
          timeslot_start: '',
          timeslot_end: '',
        },
        saturday: {
          timeslot_start: '',
          timeslot_end: '',
        },
        sunday: {
          timeslot_start: '',
          timeslot_end: '',
        },
      },
      () => getDailyScheduleData(programSetup?.daily_schedule),
    ),
    chapter_schedule: getInitialValue(
      'chapter_schedule',
      {
        monday: { chapter_start: '', chapter_end: '' },
        tuesday: { chapter_start: '', chapter_end: '' },
        wednesday: { chapter_start: '', chapter_end: '' },
        thursday: { chapter_start: '', chapter_end: '' },
        friday: { chapter_start: '', chapter_end: '' },
        saturday: { chapter_start: '', chapter_end: '' },
        sunday: { chapter_start: '', chapter_end: '' },
      },
      (_, record) => getChapterScheduleData(record.chapter_schedule),
    ),
  };

  // const form = useForm({
  //   resolver: yupResolver(ProgramSetupSchema),
  // });
  const form = useForm<IProgramSetupForm>({
    resolver: yupResolver(ProgramSetupSchema),
  });

  // console.log(form.formState.errors);

  useEffect(() => {
    form.reset(defaultValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programSetup]);

  return {
    form,
  };
};
